// ============================================================================
// CONECTA › MANUAL DO PORTAL — o montador: curadoria por módulo → `manual`
// ============================================================================
// O "Manual do Portal" é um manual da aba Manuais montado a partir de comunicados
// do Conecta que a curadoria marcou "ligados ao Portal" e classificou por módulo:
// CADA MÓDULO = 1 CAPÍTULO; dentro do capítulo, cada comunicado curado vira uma
// SEÇÃO (título = assunto) seguida dos seus blocos. Complementa a estante
// por-edição da aba Conecta (que continua existindo) — aqui o corte é por assunto
// do portal, atravessando edições.
//
// PURO de propósito (testado em `montarManualPortal.test.js`): recebe o payload já
// agrupado pelo backend (`GET /api/biblioteca/conecta/portal`) e devolve o objeto
// `manual` que o ManualLeitor consome — sem I/O, sem depender de token.
//
// ⚠️ REUSA `blocosDeComunicado` do `mapearConecta` (fonte única da tradução
//    comunicado→blocos). A diferença é só o EMBRULHO: lá 1 comunicado = 1
//    capítulo; aqui N comunicados de N edições = 1 capítulo (o módulo).
//
// ⚠️ IMAGEM CROSS-EDIÇÃO: os comunicados vêm de CHAVEs diferentes e o leitor usa
//    UM baseUrl só — então o nome do arquivo carrega a CHAVE
//    (`<chave>/c<i>-<j>.webp`), que o proxy `/portal/img/:chave/:arquivo` separa.
//    O `indice` de cada item é a POSIÇÃO do comunicado na SUA edição (o que
//    nomeou a imagem no publish) — vem do backend, não é o índice no capítulo.
// ============================================================================

import { blocosDeComunicado } from './mapearConecta';

// Módulo → id de capítulo estável para o deep-link (`?cap=mod-painel-admin`).
// Sem acento, minúsculo, só [a-z0-9-]; espaços e o resto viram hífen.
function slugModulo(modulo) {
  return 'mod-' + String(modulo || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Monta o objeto `manual` do Manual do Portal a partir do payload do backend.
 *
 * `payload` = { titulo?, finalidade?, ano?, modulos: [ { modulo,
 *   itens: [ { chave, indice, comunicado } ] } ] }.
 *
 * Os módulos já vêm na ordem dos capítulos (o backend ordena por `dtbModulos`);
 * os itens já vêm na ordem dentro do capítulo. Módulo sem item não deveria
 * chegar aqui, mas, se chegar, é pulado (capítulo vazio não entra no leitor).
 */
export function montarManualPortal(payload) {
  const p = payload || {};
  const modulos = Array.isArray(p.modulos) ? p.modulos : [];

  const capitulos = [];
  modulos.forEach((m) => {
    const itens = Array.isArray(m.itens) ? m.itens : [];
    if (itens.length === 0) return; // capítulo vazio não entra

    const blocos = [];
    itens.forEach((item) => {
      const com = item.comunicado || {};
      // Cada comunicado abre com uma seção (o assunto), separando-o do anterior
      // dentro do capítulo do módulo.
      blocos.push({ tipo: 'secao', titulo: com.assunto || 'Comunicado' });
      blocos.push(...blocosDeComunicado(com, item.indice, { prefixoImagem: `${item.chave}/` }));
    });

    capitulos.push({
      numero: String(capitulos.length + 1).padStart(2, '0'),
      id: slugModulo(m.modulo),
      titulo: m.modulo,
      blocos,
    });
  });

  return {
    id: 'portal',
    titulo: p.titulo || 'Manual do Portal Grupo Correia',
    finalidade: p.finalidade
      || 'Os comunicados do Conecta que valem como referência do portal, reunidos por módulo.',
    // Manual vivo, sem "versão": o leitor esconde a etiqueta de versão quando vazia.
    versao: '',
    ano: p.ano || String(new Date().getFullYear()),
    aplicacao: 'Guia do portal por módulo',
    capitulos,
  };
}

export default montarManualPortal;
